import {Component, OnDestroy, OnInit, ViewContainerRef, ViewEncapsulation} from '@angular/core';
import {merge, Subject} from 'rxjs';
import {AppLayoutService} from '@core/services/app-layout.service';
import {AppService} from '@core/services/app.service';
import {UserService} from '@core/services/user.service';
import {UserOnlineService} from '@core/services/user-online.service';
import {NavigationEnd, Router} from '@angular/router';
import {AuthService} from '@core/authentication/auth.service';
import {PermissionService} from '@core/authentication/permission.service';
import {NotifyService} from '@shared/services/global';
import {debounceTime, filter, take, takeUntil} from "rxjs/operators";
import {BusinessUserService} from '@app/shared/services/global/business-user.service';
import {SplitConversationService} from '@features/conversation/services/split-conversation.service';
import {DurableObjectService} from '@core/realtime/durable-object.service';
import {MemberChangedPayload} from '@shared/models/realtime/do-event.model';
import {AUTH_TRACKING_ACTION, AuthTrackingService} from '@core/authentication/auth-tracking.service';
import {PageSessionService} from '@core/authentication/page-session.service';

/**
 * MobileApp — global JS object injected by the native iOS/Android WebView.
 * Chỉ tồn tại khi app chạy trong WebView của mobile app.
 * FE13 ref: app.component.ts L47-L49
 */
declare const MobileApp: { callPhoneNumber(phone: string): void } | undefined;

@Component({
  selector: 'app-root',
  template: `
 <router-outlet></router-outlet>`,
  standalone: false,
  styles: [`
 app-root {
 height: 100%
 }
 `],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {

  private isLoaded = false;
  private destroyed = new Subject();
  /** Cờ ngăn chặn gọi logout nhiều lần đồng thời khi nhận nhiều event socket liên tiếp */
  private _isHandlingMemberLogout = false;

  constructor(
    private _app: AppService,
    private appLayoutService: AppLayoutService,
    private router: Router,
    private _viewContainerRef: ViewContainerRef,
    private _authService: AuthService,
    private _userService: UserService,
    private notifyService: NotifyService,
    private permissionService: PermissionService,
    // Inject để khởi tạo BusinessUserService — tự động load nhân viên khi DN thay đổi
    private _businessUserService: BusinessUserService,
    // Inject để khởi tạo UserOnlineService — quản lý heartbeat + tab indicator
    private _userOnlineService: UserOnlineService,
    // Inject SplitConversationService để kiểm tra cài đặt chia hội thoại và quyền nhận chia của nhân viên
    private _splitConversationService: SplitConversationService,
    // Inject DurableObjectService để lắng nghe các sự kiện realtime cấp user room
    private doService: DurableObjectService,
    // Kiểm tra quyền truy cập chạy ngầm (/auth/tracking) và thoát page khi BE yêu cầu
    private _authTrackingService: AuthTrackingService,
    private _pageSessionService: PageSessionService,
  ) {

  }

  ngOnInit() {
    this._app.setViewRootContainerRef(this._viewContainerRef);

    // Gộp cả 2 luồng (Thay đổi Business và Chuyển trang) vào làm một để xử lý tập trung
    merge(
      this._authService.activeBusiness$,
      this.router.events.pipe(filter(event => event instanceof NavigationEnd))
    ).pipe(
      debounceTime(50), // Chống rung để không gọi API 2 lần liên tiếp khi khởi tạo app
      takeUntil(this.destroyed)
    ).subscribe(() => {
      this.initializePermissions(this._authService.activeBusiness);
      this.syncOnlineStatus();
      // Khi thay đổi doanh nghiệp, reset cờ dừng chia lại để nếu DN mới có bật chia hội thoại thì tiếp tục chạy
      if (this._authService.hasJoinPages()) {
        this._splitConversationService.resetStopReAssignee();
        this._splitConversationService.intervalReAssignee();
      }
    });

    // FE13 Parity: Kiểm tra trạng thái chia hội thoại của nhân viên ngay khi khởi chạy app
    // Đảm bảo khi F5 hoặc truy cập trực tiếp bất kỳ trang nào (/conversation, /order, /setting...),
    // nếu người dùng đã đăng nhập và chọn page từ trước thì luôn kiểm tra để hiển thị nút nhận/tạm ngừng trên Header
    // và kích hoạt tiến trình tự động chia lại hội thoại (intervalReAssignee) mỗi 2 phút
    if (this._authService.hasJoinPages()) {
      this._splitConversationService.checkJoinConversation();
      this._splitConversationService.intervalReAssignee();
    }

    // Lắng nghe khi thông tin xác thực thay đổi (chọn page mới, cập nhật session...) để kiểm tra lại
    this._authService.onAuthChanged$.pipe(
      takeUntil(this.destroyed)
    ).subscribe(() => {
      if (this._authService.hasJoinPages()) {
        this._splitConversationService.resetStopReAssignee();
        this._splitConversationService.checkJoinConversation();
        this._splitConversationService.intervalReAssignee();
      }
    });

    // Lắng nghe sự kiện đổi mật khẩu, đổi quyền, khóa/xóa tài khoản qua Socket (Durable Object)
    this.listenMemberChangedDO();

    // Kiểm tra định kỳ qua BE (khoá/xoá tài khoản, giới hạn giờ, page bị gỡ/hết hạn) — bắt cả thay đổi xảy ra lúc app đang đóng
    this.listenAuthTracking();

    // Vừa bị đăng xuất tự động (trang đã tải lại) → báo lý do cho người dùng
    this.showLogoutReason();
  }

  /**
   * Khởi tạo hoặc xóa quyền tùy vào URL và Business hiện tại
   * Logic:
   * - Trang Public (Login, Checkout...): Xóa quyền để đảm bảo layout hiển thị đúng chế độ công cộng.
   * - Trang Private: Gọi API nạp quyền nếu user dùng Custom Role.
   * (PermissionService đã có cơ chế cache nên sẽ không gọi API lặp lại nếu đã nạp rồi).
   */
  private initializePermissions(activeBusiness: any) {
    const url = this.router.url;
    // Danh sách các prefix URL được xem là trang công cộng (không cần nạp quyền quản trị)
    const isPublicPage = url.startsWith('/auth') || url.startsWith('/checkout') || url.startsWith('/pricing') ||
      url.startsWith('/privacy-policy') || url.startsWith('/privacy-terms');

    if (!isPublicPage && activeBusiness && this._authService.isCustomRole()) {
      // Thực hiện nạp quyền. Service sẽ tự quyết định có gọi API thật hay lấy từ cache dựa trên ID Business.
      this.permissionService.loadRolePermissions(activeBusiness.roleId, activeBusiness.id).subscribe();
    } else {
      // Nếu là trang public hoặc không phải custom role, xóa dữ liệu quyền hiện tại để tránh hiển thị Menu nhầm lẫn
      this.permissionService.clearPermissions();
    }
  }

  /**
   * Đồng bộ trạng thái online:
   * - Trang private + đã login → bắt đầu gửi heartbeat + hiển thị ● trên tab
   * - Trang public hoặc chưa login → dừng
   */
  private syncOnlineStatus() {
    const url = this.router.url;
    const isPublicPage = url.startsWith('/auth') || url.startsWith('/checkout') || url.startsWith('/pricing') ||
      url.startsWith('/privacy-policy') || url.startsWith('/privacy-terms');

    if (!isPublicPage && this._authService.isLoginEd()) {
      this._userOnlineService.start();
    } else {
      this._userOnlineService.stop();
    }
  }

  /**
   * Lắng nghe sự kiện thay đổi nhân viên/quyền/mật khẩu (member:changed) từ Durable Object.
   * Khi user bị đổi mật khẩu, khóa tài khoản, đổi quyền hoặc xóa khỏi doanh nghiệp:
   * Tất cả các thiết bị đang đăng nhập của user đó sẽ tự động hiển thị thông báo và đăng xuất.
   */
  private listenMemberChangedDO(): void {
    if (!this.doService.isConfigured()) return;

    this.doService.onMemberChanged().pipe(
      takeUntil(this.destroyed)
    ).subscribe((event) => {
      // Bước 1: Trích xuất payload từ event socket
      const payload = event?.data?.payload;
      if (payload) {
        // Bước 2: Xử lý theo loại hành động trong payload
        this.handleMemberChanged(payload);
      }
    });
  }

  /**
   * Xử lý payload member:changed ({ type, userId, pageIds, passwordChanged... }).
   * Đảm bảo kiểm tra cực kỳ chặt chẽ để KHÔNG BAO GIỜ logout nhầm nhân viên khi có các chỉnh sửa không liên quan.
   * @param data Dữ liệu payload từ socket
   */
  private handleMemberChanged(data: MemberChangedPayload): void {
    // Bước 1: Kiểm tra tính hợp lệ của dữ liệu đầu vào
    if (!data || !data.userId || !data.type) return;

    // Bước 2: Kiểm tra chuẩn hóa ID người dùng đang đăng nhập trên thiết bị hiện tại
    const currentUid = this._authService.getUid();
    if (!currentUid || String(data.userId).trim() !== String(currentUid).trim()) {
      // Sự kiện nhắm tới user khác -> bỏ qua ngay lập tức, không gây ảnh hưởng
      return;
    }

    // Bước 3: Xử lý cập nhật tên nhân viên (editUser KHÔNG đổi mật khẩu)
    // Tuyệt đối KHÔNG đăng xuất nhân viên khi chỉ chỉnh sửa tên hiển thị
    if (data.type === 'editUser' && data.passwordChanged !== true) {
      const displayName = data.displayName ?? data.fullname ?? data.name;
      if (displayName) {
        this._authService.updateUserDisplayName(String(displayName));
      }
      return;
    }

    // Bước 4: Xử lý sự kiện ĐỔI MẬT KHẨU (editPassword hoặc editUser kèm passwordChanged === true)
    // Đăng xuất tự động tất cả các thiết bị đang dùng mật khẩu cũ để đăng nhập lại bằng mật khẩu mới
    const isPasswordChanged = data.type === 'editPassword' || (data.type === 'editUser' && data.passwordChanged === true);
    if (isPasswordChanged) {
      this.logoutWithReason('Mật khẩu của bạn đã được thay đổi. Vui lòng đăng nhập lại.');
      return;
    }

    // Bước 5: Xử lý sự kiện KHÓA hoặc XÓA tài khoản khỏi doanh nghiệp
    // Chỉ các hành động thu hồi quyền truy cập nghiêm trọng này mới kích hoạt đăng xuất
    if (data.type === 'lockUser') {
      this.logoutWithReason('Tài khoản của bạn đã bị khóa.');
      return;
    }

    if (data.type === 'removeUser') {
      this.logoutWithReason('Bạn đã bị xóa khỏi doanh nghiệp.');
      return;
    }

    // Bước 6: Xử lý thay đổi danh sách page quản lý (changeManagedPages)
    // TUYỆT ĐỐI KHÔNG LOGOUT TÀI KHOẢN khi chỉ thêm/bớt page quản lý (tránh đá văng nhân viên đang trực chat)
    if (data.type === 'changeManagedPages') {
      const currentPageIds = this._authService.getPageIds();
      // Nếu user chưa chọn page nào hoặc không có thông tin page -> bỏ qua
      if (!currentPageIds || currentPageIds.length === 0) {
        return;
      }
      // Nếu có danh sách pageIds mới cụ thể và các page đang chọn đều bị gỡ hết -> chỉ gọi outPages() để về chọn page, không logout
      if (Array.isArray(data.pageIds)) {
        const remainingPageIds = data.pageIds.map(id => String(id));
        const removedPages = currentPageIds.filter(pid => !remainingPageIds.includes(String(pid)));
        if (removedPages.length > 0) {
          this._authService.outPages();
        }
      }
      return;
    }

    // Bước 7: Các sự kiện chỉnh sửa KHÔNG liên quan khác (unlockUser, editRole, setting...)
    // Hoàn toàn KHÔNG LOGOUT nhân viên, tránh làm gián đoạn công việc đang trực chat của nhân viên
  }

  /**
   * Xử lý kết quả /auth/tracking (AuthTrackingService chạy ngầm, không chặn điều hướng):
   * - Đăng xuất: tài khoản / nhân viên bị khoá hoặc xoá (BE đã huỷ phiên).
   * - Thoát page: ngoài giờ được phép vào doanh nghiệp, page bị gỡ hoặc hết hạn → về màn chọn page, giữ đăng nhập.
   * - Khôi phục page: phiên BE mất page trong khi FE vẫn đang dùng → khôi phục ngầm như lúc mở app.
   */
  private listenAuthTracking(): void {
    this._authTrackingService.result$.pipe(
      takeUntil(this.destroyed)
    ).subscribe((result) => {
      if (result.action === AUTH_TRACKING_ACTION.LOGOUT) {
        this.logoutWithReason(result.message || 'Phiên đăng nhập không còn hợp lệ. Vui lòng đăng nhập lại.');
        return;
      }

      // Phiên BE mất page nhưng FE vẫn đang dùng → khôi phục ngầm, không làm phiền người dùng
      if (result.action === AUTH_TRACKING_ACTION.RESTORE_PAGES) {
        this._pageSessionService.restoreLostPageSession();
        return;
      }

      // Luôn báo lý do trước khi đưa về màn chọn page — không để người dùng bị đẩy ra mà không biết vì sao
      this.notifyService.warning(result.message || 'login.pageSessionEnded');
      this._pageSessionService.endPageSession();
    });

    this._authTrackingService.start();
  }

  /**
   * Hiển thị thông báo lý do đăng xuất và thực hiện logout sau một khoảng chờ ngắn (1.5s).
   * @param reason Lý do đăng xuất hiển thị cho người dùng
   */
  private logoutWithReason(reason: string): void {
    // Tránh gọi logout nhiều lần nếu nhận liên tiếp nhiều event
    if (this._isHandlingMemberLogout || this._authService.isLoggingOut) return;
    this._isHandlingMemberLogout = true;

    if (reason) {
      this.notifyService.warning(reason);
    }
    // Chờ 1.5 giây để người dùng kịp đọc thông báo toast trước khi chuyển trang; logout tải lại trang nên lý do được
    // giữ lại và hiển thị lại bằng hộp thoại ở trang đích (showLogoutReason)
    setTimeout(() => {
      this._authService.logout(reason);
    }, 1500);
  }

  /**
   * Hiển thị lý do lần bị đăng xuất tự động trước (khoá tài khoản, phiên hết hạn...) ở trang đích sau khi tải lại.
   * Bỏ qua /login-appmobile vì trang này chuyển tiếp ngay sang /auth/login của app mobile.
   */
  private showLogoutReason(): void {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter(event => !event.urlAfterRedirects.startsWith('/login-appmobile')),
      take(1),
      takeUntil(this.destroyed)
    ).subscribe(() => {
      const reason = this._authService.takeLogoutReason();
      if (reason && !this._authService.isLoginEd()) {
        this.notifyService.dialogWarning({message: reason});
      }
    });
  }

  ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

}
